<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { brandConfig, features } from '@/config/brands'
import { useToastStore } from '@/stores'
import { api } from '@/api'

interface AdSceneConfig {
  adScene?: number
  canPlay?: boolean
  joinCount?: number
  todayJoinCount?: number
}

interface AdConfig {
  configId?: string
  adOfficialId?: string | number
  iaaAdType?: number
  iaaAdSceneList?: AdSceneConfig[]
}

const props = defineProps<{
  contentId: string
  contentChapterId: string
  chapterVersionId?: string
}>()

const emit = defineEmits<{
  (e: 'completed'): void
}>()

const fallbackAdUnitPath = brandConfig.gptAd?.rewardedUnitPath || ''
const GPT_SCRIPT_ID = 'gpt-js'
const AD_REQUEST_TIMEOUT = 15_000

let gptScriptPromise: Promise<Event | void> | null = null
let slotCleanupPromise: Promise<void> = Promise.resolve()
let rewardedRequestTimeout: ReturnType<typeof setTimeout> | undefined

const { t } = useI18n()
const { $toast } = useToastStore()
const enabled = features?.enableRewardedVideoUnlock === true
const loading = ref(false)
const available = ref(false)
const adConfigId = ref('')
const adUnitPath = ref(fallbackAdUnitPath)
let rewardedSlot: GoogletagSlot | null = null
let destroyed = false
const slotEventHandlers = new Map<GoogletagSlot, Array<{ event: string; handler: (event: any) => void }>>()

function addSlotEventListener(slot: GoogletagSlot, event: string, handler: (event: any) => void) {
  const googletag = window.googletag as Googletag
  googletag.pubads().addEventListener(event, handler)
  const handlers = slotEventHandlers.get(slot) || []
  handlers.push({ event, handler })
  slotEventHandlers.set(slot, handlers)
}

function removeSlotEventListeners(slot: GoogletagSlot) {
  const googletag = window.googletag as Googletag
  for (const { event, handler } of slotEventHandlers.get(slot) || [])
    googletag.pubads().removeEventListener(event, handler)
  slotEventHandlers.delete(slot)
}

function clearRewardedRequestTimeout() {
  if (rewardedRequestTimeout !== undefined) {
    clearTimeout(rewardedRequestTimeout)
    rewardedRequestTimeout = undefined
  }
}

function startRewardedRequestTimeout() {
  clearRewardedRequestTimeout()
  rewardedRequestTimeout = setTimeout(async () => {
    rewardedRequestTimeout = undefined
    if (!loading.value)
      return

    $toast(t('RewardedVideoUnavailable'))
    if (rewardedSlot)
      await destroyRewardedSlot(rewardedSlot)
    else
      loading.value = false
  }, AD_REQUEST_TIMEOUT)
}

async function loadAdConfig() {
  if (!enabled)
    return
  try {
    const { data } = await api.get<any, { data?: AdConfig[] }>('/iaa/ad/getConfigInfo')
    const config = (data || []).find(item => item.iaaAdType === 2)
    const scene = config?.iaaAdSceneList?.find(item => item.adScene === 2)
    const remaining = Number(scene?.joinCount ?? 0) - Number(scene?.todayJoinCount ?? 0)
    if (!config?.configId || !scene?.canPlay || remaining <= 0)
      return

    adConfigId.value = config.configId
    const officialAdUnitPath = String(config.adOfficialId ?? '').trim()
    adUnitPath.value = officialAdUnitPath.length >= 5 ? officialAdUnitPath : fallbackAdUnitPath
    available.value = !!adUnitPath.value
  }
  catch {
    available.value = false
  }
}

function loadGptScript() {
  if (gptScriptPromise)
    return gptScriptPromise

  window.googletag = window.googletag || { cmd: [] }
  if ((window.googletag as Googletag).apiReady)
    return Promise.resolve()

  const existingScript = (document.getElementById(GPT_SCRIPT_ID)
    || document.querySelector('script[src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"]')) as HTMLScriptElement | null
  if (existingScript) {
    gptScriptPromise = new Promise((resolve, reject) => {
      existingScript.addEventListener('load', resolve, { once: true })
      existingScript.addEventListener('error', () => reject(new Error('Failed to load GPT')), { once: true })
    })
    return gptScriptPromise
  }

  gptScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = GPT_SCRIPT_ID
    script.async = true
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
    script.crossOrigin = 'anonymous'
    script.onload = resolve
    script.onerror = () => reject(new Error('Failed to load GPT'))
    document.head.appendChild(script)
  })
  return gptScriptPromise
}

async function destroyRewardedSlot(slot = rewardedSlot) {
  clearRewardedRequestTimeout()
  if (!slot || !window.googletag)
    return
  if (rewardedSlot === slot)
    rewardedSlot = null
  loading.value = true

  slotCleanupPromise = slotCleanupPromise.then(() => new Promise<void>((resolve) => {
    window.googletag?.cmd.push(() => {
      removeSlotEventListeners(slot)
      ;(window.googletag as Googletag | undefined)?.destroySlots([slot])
      resolve()
    })
  }))

  try {
    await slotCleanupPromise
  }
  finally {
    if (!rewardedSlot)
      loading.value = false
  }
}

async function startAdRecord(): Promise<string | null> {
  const { data } = await api.post<any, { data?: { canPlay?: boolean; memberAppAdRecordId?: string } }>('/iaa/ad/play', {
    appAdConfigId: adConfigId.value,
    adScene: 2,
  })
  if (!data?.canPlay || !data.memberAppAdRecordId)
    return null
  return data.memberAppAdRecordId
}

async function finishAdRecord(memberAppAdRecordId: string) {
  let chapterVersionId = props.chapterVersionId
  if (!chapterVersionId) {
    const { data } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterVersionId = data?.records?.find((item: any) => String(item.id) === String(props.contentChapterId))?.chapterVersionId
  }

  if (!chapterVersionId)
    throw new Error('Missing rewarded chapter version')

  const body: Record<string, unknown> = {
    memberAppAdRecordId,
    contentIdStr: props.contentId,
    adScene: 2,
    // 阅读场景的 finish 接口需要明确要解锁的章节；不能依赖 awardType，
    // 因为部分服务端配置未返回该字段。
    chapterVersionInfoList: [{
      contentChapterId: props.contentChapterId,
      contentChapterVersionId: chapterVersionId,
    }],
  }

  const { data } = await api.post<any, { data?: { canFinish?: boolean; message?: string } }>('/iaa/ad/finish', body)
  if (!data?.canFinish)
    throw new Error(data?.message || 'Rewarded chapter unlock failed')

  emit('completed')
  $toast(t('RewardedVideoCompleted'))
}

function prepareRewardedAd() {
  const googletag = window.googletag as Googletag
  googletag.cmd.push(() => {
    if (destroyed || rewardedSlot)
      return

    // 按 GPT 官方 Rewarded 示例定义站外广告位，不使用普通 1×1 div Slot。
    rewardedSlot = googletag.defineOutOfPageSlot(
      adUnitPath.value,
      googletag.enums.OutOfPageFormat.REWARDED,
    )
    if (!rewardedSlot) {
      clearRewardedRequestTimeout()
      loading.value = false
      $toast(`${t('RewardedVideoUnavailable')} [UNSUPPORTED]`, 3000)
      return
    }

    const slot = rewardedSlot
    // 一次展示对应一条 play 记录。不要使用组件级 ref，否则 closed 事件可能
    // 在 finish 请求组装参数前清空/覆盖它。
    let adRecordId = ''
    slot.addService(googletag.pubads())
    addSlotEventListener(slot, 'rewardedSlotReady', async (event: any) => {
      if (event.slot !== slot)
        return
      try {
        // 与 Flutter 一致：广告真正展示前创建本次播放记录。
        const recordId = await startAdRecord()
        if (destroyed || rewardedSlot !== slot)
          return
        if (!recordId) {
          $toast(t('RewardedVideoUnavailable'))
          await destroyRewardedSlot(slot)
          return
        }
        adRecordId = recordId
        clearRewardedRequestTimeout()
        loading.value = false
        const visible = event.makeRewardedVisible()
        if (!visible) {
          $toast(t('RewardedVideoUnavailable'))
          await destroyRewardedSlot(slot)
        }
      }
      catch {
        $toast(t('RewardedVideoUnavailable'))
        await destroyRewardedSlot(slot)
      }
    })
    addSlotEventListener(slot, 'rewardedSlotGranted', async (event: any) => {
      if (event.slot !== slot)
        return
      try {
        // 与 Flutter 的 onUserEarnedReward 一致：仅广告平台确认授予奖励后通知后端发奖/解锁。
        if (!adRecordId)
          throw new Error('Missing rewarded ad record')
        await finishAdRecord(adRecordId)
      }
      catch {
        $toast(t('RewardedVideoUnavailable'))
      }
    })
    addSlotEventListener(slot, 'rewardedSlotClosed', async (event: any) => {
      if (event.slot !== slot)
        return
      // GPT 不会为已关闭的 Rewarded Slot 触发 refresh 请求；必须销毁它，
      // 下次用户点击时重新定义一个新的 Rewarded Slot。
      await destroyRewardedSlot(slot)
    })
    addSlotEventListener(slot, 'slotRenderEnded', async (event: any) => {
      if (event.slot !== slot)
        return
      if (event.isEmpty) {
        $toast(`${t('RewardedVideoUnavailable')} [NO_FILL]`, 3000)
        // 空填充的 Rewarded Slot 也不能复用；清理后下次点击才会定义新 Slot。
        await destroyRewardedSlot(slot)
      }
    })

    googletag.enableServices()
    googletag.display(slot)
  })
}

async function handleWatchAd() {
  if (!enabled || loading.value)
    return
  loading.value = true
  try {
    await slotCleanupPromise
    await loadGptScript()
    if (destroyed)
      return
    if (rewardedSlot) {
      // 关闭事件的销毁命令尚在 GPT 队列时，不让按钮永久处于 loading。
      loading.value = false
      return
    }
    startRewardedRequestTimeout()
    prepareRewardedAd()
  }
  catch {
    clearRewardedRequestTimeout()
    loading.value = false
    $toast(`${t('RewardedVideoUnavailable')} [SCRIPT_ERROR]`, 3000)
  }
}

onBeforeUnmount(() => {
  destroyed = true
  destroyRewardedSlot()
})

onMounted(loadAdConfig)
</script>

<template>
  <div v-if="enabled && available" class="mt-12px text-center">
    <button
      type="button"
      class="rewarded-video-unlock inline-flex items-center justify-center w-300px max-w-full h-40px px-24px rounded-full text-15px font-medium"
      :disabled="loading"
      @click.stop="handleWatchAd"
    >
      <span v-if="loading" class="rewarded-video-spinner-wrap">
        <BasicSpinner class="rewarded-video-spinner" />
      </span>
      <span v-else>{{ t('Ads') }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.rewarded-video-unlock {
  color: var(--rewarded-video-color, var(--theme-desc-color, #65686F));
  border: 1px solid var(--rewarded-video-color, var(--theme-desc-color, #65686F));
  background: var(--rewarded-video-bg, rgba(255, 255, 255, 0.55));

  &:disabled {
    opacity: 0.6;
  }
}

.rewarded-video-spinner-wrap {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.rewarded-video-spinner {
  flex: none;
  transform: scale(0.45);
  transform-origin: center;
}
</style>
